import { Stack, Typography, Box, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useState } from 'react';
import Iconify from 'src/components/iconify';
import { getPreScreeningQuestions } from 'src/services/candidate.service';
import PropTypes from 'prop-types';
import FallbackUI from './fallback-ui';
import ProfileLoader from './loader';
import { CandidateProfileContext } from './context/candidate-profile-context';
import { fThousandSeparator } from 'src/utils/format-number';

const useStyles = makeStyles(theme => ({
  mainDiv: {
    gap: 24,
  },
  qualifiedDiv: {
    padding: '11.5px 16px',
    background: theme.palette.content.primaryGreen2,
    color: theme.palette.background.primaryGreen1,
  },
  disqualifiedDiv: {
    padding: '11.5px 16px',
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
  },
  screeningItemDiv: {
    borderRadius: 6,
    padding: 20,
    border: `1px solid ${theme.palette.border.primaryGreen1}`,
  },
  screeningItemDivBasic: {
    borderRadius: 6,
    padding: 20,
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
  },
  screeningItemDivError: {
    borderRadius: 6,
    padding: 20,
    border: `1px solid ${theme.palette.border.primaryRed1}`,
  },
  qualifiedIndicatorDiv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    left: '89%',
    gap: 6,
    padding: '6px 8px',
    marginTop: -10,
    width: 90,
    borderRadius: 22,
    background: theme.palette.content.primaryGreen2,
    color: theme.palette.background.primaryGreen1,
    [theme.breakpoints.down('lg')]: {
      left: '85%',
    },
    [theme.breakpoints.down('md')]: {
      left: '80%',
    },
  },
  tickIcon: {
    color: theme.palette.background.primaryGreen1,
  },
  crossIcon: {
    color: theme.palette.background.primaryRed1,
  },
  disqualifiedIndicatorDiv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    left: '87%',
    gap: 6,
    padding: '6px 8px',
    marginTop: -10,
    width: 105,
    borderRadius: 22,
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
    [theme.breakpoints.down('lg')]: {
      left: '82%',
    },
    [theme.breakpoints.down('md')]: {
      left: '80%',
    },
  },
  questionDiv: {
    marginTop: -16,
    marginBottom: 24,
    display: 'inline-block',
    gap: 6,
    width: '75%',
  },
  qualifyTag: {
    marginLeft: '8px',
    padding: '4px 10px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 15,
    display: 'inline-flex',
    marginBottom: '8px',
    background: theme.palette.background.primaryAccent2,
  },
  responseDiv: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'space-between',
  },
  actualResponseDiv: {
    gap: 10,
    flexGrow: 1,
  },
  idealResponseDiv: {
    color: theme.palette.content.primaryBlack3,
    gap: 10,
    flexGrow: 1,
  },
}));

const QualifiedTag = ({ success }) => {
  const classes = useStyles();
  return (
    <Stack
      className={
        success
          ? classes.qualifiedIndicatorDiv
          : classes.disqualifiedIndicatorDiv
      }
    >
      {success ? (
        <Iconify
          width={16}
          height={16}
          className={classes.tickIcon}
          icon="tabler:circle-check-filled"
        />
      ) : (
        <Iconify
          width={16}
          height={16}
          className={classes.crossIcon}
          icon="tabler:circle-x-filled"
        />
      )}
      <Typography variant="caption" sx={{ fontSize: '12px' }}>
        {success ? 'Qualified' : 'Disqualified'}
      </Typography>
    </Stack>
  );
};

const BOOLEAN_RESPONSES = {
  YES: 'Yes',
  TRUE: 'Yes',
  NO: 'No',
  FALSE: 'No',
};

const PreScreeningGroup = ({ jobId }) => {
  // hooks
  const classes = useStyles();
  const candidateDetails = useContext(CandidateProfileContext)?.obj;

  // states
  const [preScreeningData, setPreScreeningData] = useState(null);
  const [isEvaluated, setIsEvaluated] = useState(false);
  const [qualified, setQualified] = useState(null);
  const [hasSolutions, setHasSolutions] = useState(false);
  const [isPreScreeningLoading, setIsPreScreeningLoading] = useState(true);

  // effects
  useEffect(() => {
    setIsPreScreeningLoading(true);

    getPreScreeningQuestions(jobId, candidateDetails.candidate_info.user_id)
      .then(res => {
        if (res.success) {
          const _prescreeningQuestions = res?.body?.questions?.map(item => {
            const question = {
              questionId: item?.question_id,
              responseParameters: item?.response_parameters,
              question: item?.text,
              isQualifyingQuestion: item?.is_qualifying_question,
              responseType: item?.response_type,
              response: null,
            };

            return question;
          });

          res?.body?.solutions?.forEach((solution, index) => {
            const questionObj = _prescreeningQuestions.find(
              question => question.questionId === solution.question_id,
            );

            if (questionObj) {
              questionObj.response = {
                responseText: solution?.response_text,
                isEvaluated: solution?.is_evaluated,
                success: solution?.score === 1,
              };
            }
          });

          setHasSolutions(res?.body?.solutions?.length > 0);
          setQualified(res?.body?.is_qualified);
          setIsEvaluated(res?.body?.is_evaluated);
          setPreScreeningData(_prescreeningQuestions);
        } else {
          setHasSolutions(false);
          setQualified(null);
          setPreScreeningData([]);
          throw new Error(res.message);
        }
      })
      .catch(err => console.log(err))
      .finally(() => {
        setIsPreScreeningLoading(false);
      });
  }, [candidateDetails.candidate_info.user_id, jobId]);

  const PreScreeningGroupSkeleton = (
    <Stack spacing="24px">
      <Skeleton
        variant="rectangular"
        width="100%"
        height={48}
        sx={{
          backgroundColor: '#FAFAFA',
        }}
      />
      <Stack spacing="20px">
        <Stack
          sx={{
            border: theme => `1px solid ${theme.palette.border.primaryBlack3}`,
            padding: '20px',
            height: '130px',
            position: 'relative',
            justifyContent: 'space-between',
          }}
        >
          <Skeleton
            width={90}
            height={28}
            sx={{
              position: 'absolute',
              right: '12px',
              top: '12px',
              borderRadius: '22px',
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              gap: '6px',
            }}
          >
            <Skeleton width={78} height={9} />
            <Skeleton width={53} height={20} sx={{ borderRadius: '15px' }} />
          </Stack>
          <Stack className={classes.responseDiv}>
            <Stack className={classes.actualResponseDiv}>
              <Skeleton width={130} height={9} />
              <Skeleton width={12} height={9} />
            </Stack>
            <Stack className={classes.idealResponseDiv}>
              <Skeleton width={130} height={9} />
              <Skeleton width={12} height={9} />
            </Stack>
          </Stack>
        </Stack>
        <Stack
          sx={{
            border: theme => `1px solid ${theme.palette.border.primaryBlack3}`,
            padding: '20px',
            height: '130px',
            position: 'relative',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              gap: '6px',
            }}
          >
            <Skeleton width={100} height={9} />
            <Skeleton width={53} height={20} sx={{ borderRadius: '15px' }} />
          </Stack>
          <Stack className={classes.responseDiv}>
            <Stack className={classes.actualResponseDiv}>
              <Skeleton width={130} height={9} />
              <Skeleton width={12} height={9} />
            </Stack>
            <Stack className={classes.idealResponseDiv}>
              <Skeleton width={130} height={9} />
              <Skeleton width={12} height={9} />
            </Stack>
          </Stack>
        </Stack>
        <Stack
          sx={{
            border: theme => `1px solid ${theme.palette.border.primaryBlack3}`,
            padding: '20px',
            height: '130px',
            position: 'relative',
            justifyContent: 'space-between',
          }}
        >
          <Skeleton
            width={90}
            height={28}
            sx={{
              position: 'absolute',
              right: '12px',
              top: '12px',
              borderRadius: '22px',
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              gap: '6px',
            }}
          >
            <Skeleton width={78} height={9} />
            <Skeleton width={53} height={20} sx={{ borderRadius: '15px' }} />
          </Stack>
          <Stack className={classes.responseDiv}>
            <Stack className={classes.actualResponseDiv}>
              <Skeleton width={130} height={9} />
              <Skeleton width={12} height={9} />
            </Stack>
            <Stack className={classes.idealResponseDiv}>
              <Skeleton width={130} height={9} />
              <Skeleton width={12} height={9} />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={48}
        sx={{
          backgroundColor: '#F7F7F7',
        }}
      />
    </Stack>
  );

  return isPreScreeningLoading ? (
    <Stack
      width="100%"
      height="100%"
      justifyContent="center"
      alignItems="center"
    >
      Loading...
    </Stack>
  ) : (
    <Stack className={classes.mainDiv}>
      {qualified !== null && (
        <Stack
          className={qualified ? classes.qualifiedDiv : classes.disqualifiedDiv}
        >
          <Typography variant="subheading1">
            {qualified ? 'Qualified' : 'Disqualified'}
          </Typography>
        </Stack>
      )}

      {(!preScreeningData ||
        preScreeningData?.length === 0 ||
        !hasSolutions ||
        isEvaluated === null) && <FallbackUI content="No data found" />}

      {isEvaluated !== null &&
        preScreeningData?.map((item, index) => {
          if (item?.response === null) return null;

          let screeningItemDivClass = classes.screeningItemDivBasic;

          if (item?.isQualifyingQuestion && item?.response?.isEvaluated) {
            screeningItemDivClass = classes.screeningItemDiv;
            if (!item?.response?.success) {
              screeningItemDivClass = classes.screeningItemDivError;
            }
          }

          return (
            <Stack key={index} className={screeningItemDivClass}>
              {item?.isQualifyingQuestion &&
                item?.response &&
                item?.response?.isEvaluated && (
                  <QualifiedTag success={item?.response?.success} />
                )}

              <Stack
                sx={{
                  marginTop: item?.isQualifyingQuestion
                    ? '-16px'
                    : '0px !important',
                }}
                className={classes.questionDiv}
              >
                <Typography
                  variant="paragraph"
                  sx={{ fontWeight: 600, margin: 0 }}
                >
                  {item?.question}
                  {item.isQualifyingQuestion && (
                    <Box className={classes.qualifyTag}>
                      <Typography variant="caption" sx={{ fontSize: '10px' }}>
                        Must Qualify
                      </Typography>
                    </Box>
                  )}
                </Typography>
              </Stack>
              <Stack className={classes.responseDiv}>
                <Stack className={classes.actualResponseDiv}>
                  <Typography variant="caption" sx={{ fontSize: '12px' }}>
                    Candidate response
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {item?.responseType === 'NUMERIC'
                      ? fThousandSeparator(item?.response?.responseText)
                      : item?.responseType === 'BOOLEAN'
                        ? BOOLEAN_RESPONSES[item?.response?.responseText]
                        : item?.response?.responseText}
                  </Typography>
                </Stack>
                {item?.responseType !== 'TEXT' && (
                  <Stack className={classes.idealResponseDiv}>
                    <Typography variant="caption" sx={{ fontSize: '12px' }}>
                      Ideal response
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {item?.responseType === 'NUMERIC' &&
                        `${fThousandSeparator(
                          item?.responseParameters?.MIN_VALUE,
                        )}-${fThousandSeparator(
                          item?.responseParameters?.MAX_VALUE,
                        )}`}
                      {item?.responseType === 'BOOLEAN' &&
                        (BOOLEAN_RESPONSES[
                          item?.responseParameters?.IDEAL_ANSWER
                        ] ||
                          item?.responseParameters?.IDEAL_ANSWER)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          );
        })}
    </Stack>
  );
};

export default PreScreeningGroup;

PreScreeningGroup.propTypes = {
  jobId: PropTypes.string.isRequired,
};

QualifiedTag.propTypes = {
  success: PropTypes.bool.isRequired,
};
